import { useCallback, useEffect, useState } from 'react';

export type MenuPlacement = 'top' | 'bottom';

export interface MenuPositioning {
  placement: MenuPlacement;
  maxHeight: number;
  isCalculated: boolean;
}

// フックの設定値を内部で定義
const MENU_CONFIG = {
  minHeight: 100,
  maxHeight: 300,
  viewportMargin: 20,
  itemHeight: 35,
} as const;

export const useMenuPositioning = (
  triggerRef: React.RefObject<HTMLElement>
) => {
  const [positioning, setPositioning] = useState<MenuPositioning>({
    placement: 'bottom',
    maxHeight: MENU_CONFIG.maxHeight,
    isCalculated: false,
  });

  const calculateOptimalPosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const trigger = triggerRef.current;
    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // 上下の利用可能空間を計算
    const spaceAbove = rect.top - MENU_CONFIG.viewportMargin;
    const spaceBelow =
      viewportHeight - rect.bottom - MENU_CONFIG.viewportMargin;

    // 常に最大高さで計算（アイテム数に依存しない）
    const idealHeight = MENU_CONFIG.maxHeight;

    // 最適な配置を決定
    let placement: MenuPlacement;
    let calculatedMaxHeight: number;

    if (spaceBelow >= idealHeight) {
      // 下に十分な空間がある場合
      placement = 'bottom';
      calculatedMaxHeight = Math.min(idealHeight, spaceBelow);
    } else if (spaceAbove >= idealHeight) {
      // 上に十分な空間がある場合
      placement = 'top';
      calculatedMaxHeight = Math.min(idealHeight, spaceAbove);
    } else {
      // どちらも十分でない場合、より広い方を選択
      if (spaceBelow >= spaceAbove) {
        placement = 'bottom';
        calculatedMaxHeight = Math.max(MENU_CONFIG.minHeight, spaceBelow);
      } else {
        placement = 'top';
        calculatedMaxHeight = Math.max(MENU_CONFIG.minHeight, spaceAbove);
      }
    }

    // 最終的な高さ制限を適用
    calculatedMaxHeight = Math.min(calculatedMaxHeight, MENU_CONFIG.maxHeight);
    calculatedMaxHeight = Math.max(calculatedMaxHeight, MENU_CONFIG.minHeight);

    setPositioning({
      placement,
      maxHeight: calculatedMaxHeight,
      isCalculated: true,
    });
  }, [triggerRef]);

  // 初回計算とリサイズ監視
  useEffect(() => {
    if (!triggerRef.current) {
      return;
    }

    calculateOptimalPosition();
    const handleResize = () => {
      calculateOptimalPosition();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateOptimalPosition]);

  return {
    positioning,
    calculateOptimalPosition,
  };
};
